import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { Editor } from '@tiptap/react';
import type React from 'react';
import type { Section, Tender } from '../../../types/tender';
import { ReviewSectionRow } from '../ReviewSectionRow';
import { getFileId } from './utils';

interface TenderSectionsListProps {
  sections: Section[];
  visibleSections: Section[];
  activeId: string | null;
  expandedSectionIds: Set<string>;

  // biome-ignore lint/suspicious/noExplicitAny: complex type
  comparisonData: any;
  tenderId: string;
  tender: Tender;
  account: import('../../../types/auth').User | null;
  editingSectionId: string | null;
  editContent: string;
  setEditContent: (c: string) => void;

  activeEditor: Editor | null;

  setActiveEditor: (e: Editor | null) => void;
  showDiffs: Record<string, boolean>;
  setShowDiffs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  inlineResolved: Record<string, boolean>;
  chatSectionId: string | null;
  setChatSectionId: (id: string | null) => void;
  downloadingSectionId: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: complex type
  sectionHandlers: any;
  historyComparisonSectionId: string | null;
  canEditAllSections: boolean;
  setExpandedSectionIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleOpenAddDialog: (index: number) => void;
}

export const TenderSectionsList: React.FC<TenderSectionsListProps> = ({
  sections,
  visibleSections,
  activeId,
  expandedSectionIds,
  comparisonData,
  tenderId,
  tender,
  account,
  editingSectionId,
  editContent,
  setEditContent,
  activeEditor,
  setActiveEditor,
  showDiffs,
  setShowDiffs,
  inlineResolved,
  chatSectionId,
  setChatSectionId,
  downloadingSectionId,
  sectionHandlers,
  historyComparisonSectionId,
  canEditAllSections,
  setExpandedSectionIds,
  handleOpenAddDialog,
}) => {
  const narrativeSections = visibleSections.filter((s) => !getFileId(s.title));
  const formSections = visibleSections.filter((s) => !!getFileId(s.title));

  const isUnfilled = (section: Section) => {
    if (!section.content) {
      return true;
    }
    const text = section.content.replace(/<[^>]*>?/gm, '').trim();
    const lower = text.toLowerCase();
    return (
      text === '' ||
      text.includes('___') ||
      text === '[]' ||
      text === '[ ]' ||
      lower.includes('placeholder') ||
      lower === 'n/a' ||
      lower === 'not applicable'
    );
  };

  const filledFormSections = formSections.filter((s) => !isUnfilled(s));
  const reorderedSections = [...narrativeSections, ...filledFormSections];

  return (
    <SortableContext
      items={reorderedSections.map((s) => s.id)}
      strategy={verticalListSortingStrategy}
    >
      {reorderedSections.map((section, _index) => {
        const fullIndex = sections.findIndex((s) => s.id === section.id);
        const currentFileId = getFileId(section.title);
        const prevFileId =
          _index > 0 ? getFileId(reorderedSections[_index - 1]?.title || '') : null;
        const isFormHeader = currentFileId && currentFileId !== prevFileId;
        const isMainHeader = _index === 0 && !currentFileId;
        const showSeparator = isFormHeader && !!prevFileId && currentFileId !== prevFileId;
        const isFirstForm = isFormHeader && !prevFileId;

        let formMetrics: {
          totalTags: number;
          filledTags: number;
          emptyTags: number;
        } | null = null;
        if (isFormHeader && currentFileId && tender.anchored_forms) {
          const form = tender.anchored_forms.find((f) => f.filename === currentFileId);
          if (form) {
            formMetrics = form.metrics || {
              totalTags: 0,
              filledTags: 0,
              emptyTags: 0,
            };
          }
        }

        const nextSectionIndex =
          fullIndex === sections.length - 1
            ? section.index + 1
            : sections[fullIndex + 1]?.index || section.index + 1;

        return (
          <ReviewSectionRow
            key={`frag-${section.id}`}
            section={section}
            fullIndex={fullIndex}
            nextSectionIndex={nextSectionIndex}
            activeId={activeId}
            expandedSectionIds={expandedSectionIds}
            comparisonData={comparisonData}
            tenderId={tenderId}
            tender={tender}
            account={account}
            editingSectionId={editingSectionId}
            editContent={editContent}
            setEditContent={setEditContent}
            activeEditor={activeEditor}
            setActiveEditor={setActiveEditor}
            showDiffs={showDiffs}
            setShowDiffs={setShowDiffs}
            inlineResolved={inlineResolved}
            chatSectionId={chatSectionId}
            setChatSectionId={setChatSectionId}
            downloadingSectionId={downloadingSectionId}
            sectionHandlers={sectionHandlers}
            historyComparisonSectionId={historyComparisonSectionId}
            canEditAllSections={canEditAllSections}
            setExpandedSectionIds={setExpandedSectionIds}
            handleOpenAddDialog={handleOpenAddDialog}
            isFirstForm={!!isFirstForm}
            showSeparator={!!showSeparator}
            isMainHeader={!!isMainHeader}
            isFormHeader={!!isFormHeader}
            currentFileId={currentFileId}
            formMetrics={formMetrics}
          />
        );
      })}
    </SortableContext>
  );
};
